import {ReactElement, useEffect, useRef, useState} from "react";
import {Body, Footer, FooterItem, Header} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import {Field, File, FileUpload, Input, Label, Message} from "@zendeskgarden/react-forms";
import {Separator} from "@zendeskgarden/react-dropdowns";
import {Inline} from "@zendeskgarden/react-loaders";
import {toast} from "react-toastify";
import {COLORS} from "../../../../constants/Colors";
import {
    AcquisitionPropertyTemplateName,
    useGenerateAcquisitionAccountValuesFileUploadUrlMutation,
    useProcessAcquisitionAccountValuesFileMutation
} from "../../../../__generated__/generated_types";
import {FileUploadModalProps} from "./FileUploadModalProps";
import {useFetchTemplateCsv} from "../../logic/useFetchTemplateCsv";

export function DirectChartOfAccountsFileUpload(props: FileUploadModalProps): ReactElement {
    const {propertyId, onModalClose} = props;

    const [accountValuesFile, setAccountValuesFile] = useState<File | undefined>(undefined);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const accountValuesInputRef = useRef<HTMLInputElement | null>(null);

    const [generateUrl, {loading: loadingUrl, data: urlData}] = useGenerateAcquisitionAccountValuesFileUploadUrlMutation({
        notifyOnNetworkStatusChange: true
    });

    const [processFile, {loading: loadingProcess}] = useProcessAcquisitionAccountValuesFileMutation({
        notifyOnNetworkStatusChange: true
    });

    const {fetchTemplate} = useFetchTemplateCsv("accountvalues");

    useEffect(() => {
        if (urlData && !loadingUrl && isUploading) {
            uploadFiles();
        }
    }, [urlData, loadingUrl]);

    const handleAccountValuesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setAccountValuesFile(event.target.files[0]);
        }
    };

    const reset = () => {
        setAccountValuesFile(undefined);
        setIsUploading(false);
        onModalClose();
    };

    const uploadFiles = () => {
        if(!urlData || !accountValuesFile) {
            setIsUploading(false);
            return;
        }

        // Get URLs and keys
        const accountValuesUrl = urlData.generateAcquisitionAccountValuesFileUploadUrl.signedUrl;
        const accountValuesKey = urlData.generateAcquisitionAccountValuesFileUploadUrl.key;

        // Create array of upload promises
        fetch(accountValuesUrl, {
            method: 'PUT',
            body: accountValuesFile,
            headers: {
                'Content-Type': accountValuesFile.type,
            }
        })
        .then(() => {
            return processFile({
                variables: {
                    propertyId,
                    fileKey: accountValuesKey
                }
            });
        })
        .then(res => {
            if(res.data?.processAcquisitionAccountValuesFile) {
                props.jobCreated(res.data.processAcquisitionAccountValuesFile.id, props.propertyId);
                toast.success("Account values uploaded successfully");
                reset();
            }
        })
        .catch((error) => {
            console.error("Error uploading file:", error);
            toast.error("Error uploading file");
        })
        .finally(() => {
            setIsUploading(false);
        });
    };

    const handleSubmit = () => {
        if (!accountValuesFile) {
            toast.error("Please select an account values file.");
            return;
        }

        setIsUploading(true);
        generateUrl({
            variables: {
                propertyId
            }
        });
    };

    const renderSize = (size: number): string => {
        let i = 0;
        while(size > 1024 && i < 3) {
            size /= 1024;
            i++;
        }

        if(i === 0) {
            return `${size} bytes`;
        } else if(i === 1) {
            return `${size.toFixed(2)} KB`;
        } else {
            return `${size.toFixed(2)} MB`;
        }
    };

    return (
            <>
                <Header>Upload Property Files</Header>
                <Body>
                    <form>
                        <Field>
                            <Label>Account Values File</Label>
                            <FileUpload
                                    style={{display: !accountValuesFile ? "block" : "none"}}
                                    role="button"
                                    onClick={() => {
                                        if(accountValuesInputRef) {
                                            accountValuesInputRef.current?.click();
                                        }
                                    }}
                            >
                                <Message>Select Account Values CSV</Message>
                                <Input ref={accountValuesInputRef} style={{display:"none"}} type="file" accept=".csv, .xls, .xlsx" onChange={handleAccountValuesChange} />
                            </FileUpload>
                            {accountValuesFile &&
                                    <div style={{fontSize: "0.9rem", textAlign: "center"}}>
                                        {accountValuesFile.name} ({renderSize(accountValuesFile.size)})
                                        <Button isBasic onClick={() => {
                                            setAccountValuesFile(undefined);
                                            if(accountValuesInputRef.current) {
                                                const input = accountValuesInputRef.current;
                                                if(input.files && input.files.length > 0) {
                                                    input.value = "";
                                                }
                                            }
                                        }}>Remove</Button>
                                    </div>
                            }
                        </Field>
                    </form>
                    <div style={{marginTop: "0.5rem"}}><Button isLink onClick={() => fetchTemplate(propertyId, AcquisitionPropertyTemplateName.AccountValues)}>Download Account Values Template</Button></div>
                </Body>
                <Separator />
                <Footer>
                    <FooterItem>
                        <Button onClick={reset} isBasic disabled={isUploading || loadingUrl || loadingProcess}>
                            Cancel
                        </Button>
                    </FooterItem>
                    <FooterItem>
                        <Button isPrimary onClick={handleSubmit} disabled={!accountValuesFile || isUploading || loadingUrl || loadingProcess}>
                            {
                                (isUploading || loadingUrl || loadingProcess)
                                        ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading"/>
                                        : <span>Upload</span>
                            }
                        </Button>
                    </FooterItem>
                </Footer>
            </>
    );
}